import { h } from 'preact';
import './themes/newsela/quiz.scss';
import * as is from 'utilities/is';
import { now } from 'utilities/time';
import { Buttons } from './buttons';
import { formToObject } from 'utilities/data';
import { QuizProgress } from './quiz_progress';
import { PreSubmitNotice } from './presubmit_notice';
import { GradeCalculatorFactory, QuizSceneController } from './models';
import { BaseAnnotation } from 'rendering/annotations/base_annotation';
import { ANNOTATION_EVENT, APPLICATION_EVENT, PLAYER_CONTROL_EVENT } from 'constants/events';
import { COMPOSITION_TYPES, LIFECYCLES } from 'constants/annotations';
import { formatResponse, RECORDING_STATUSES } from './reporting';

export class QuizAnnotation extends BaseAnnotation {
    constructor(...args) {
        super(...args);

        let { descendants, initialFormData, initialState = {} } = this.props;
        const { level: currentLevel = '' } = this.props?.data?.state || {};

        if (
            (initialState.currentLevel || currentLevel) && 
            initialState.currentLevel !== currentLevel
        ) {
            this.state = { ...this.defaultState };
            initialFormData = {};
            initialState = {};
        }

        this.history = [];
        this.initializeGrading();
        this.quizController = new QuizSceneController({ descendants, initialFormData, initialState });

        this.state = {
            currentLevel,
            ...this.state,
            ...this.quizController.currentScene
        }

        this.eventBus.on(ANNOTATION_EVENT.NEXT_ITEM, this.onNextItem);
        this.eventBus.on(ANNOTATION_EVENT.PREV_ITEM, this.onPrevItem);
        this.eventBus.on(ANNOTATION_EVENT.RETRY_QUIZ, this.onRetryQuiz);
        this.eventBus.on(ANNOTATION_EVENT.RESET_FORM, this.onFormReset);
        this.eventBus.on(ANNOTATION_EVENT.SUBMIT_FORM, this.initiateSubmit);
        this.eventBus.on(ANNOTATION_EVENT.COMPLETE_QUIZ, this.onCompleteQuiz);
        this.eventBus.on(ANNOTATION_EVENT.NAVIGATE_SUB_ELEMENT, this.onNavigateSubElement);
        this.eventBus.on(APPLICATION_EVENT.LEVEL_CHANGED, this.onLevelChanged);
    }

    initializeGrading() {
        const { passingThreshold = 0, evaluateQuiz = true } = this.options;

        this.calculator = GradeCalculatorFactory.create({ passingThreshold, evaluateQuiz });
        this.calculator.answerKey = this.questions;
        this.calculator.currentAnswers = this.formData;
    }

    componentWillUnmount() {
        this.eventBus.off(ANNOTATION_EVENT.NEXT_ITEM, this.onNextItem);
        this.eventBus.off(ANNOTATION_EVENT.PREV_ITEM, this.onPrevItem);
        this.eventBus.off(ANNOTATION_EVENT.RETRY_QUIZ, this.onRetryQuiz);
        this.eventBus.off(ANNOTATION_EVENT.RESET_FORM, this.onFormReset);
        this.eventBus.off(ANNOTATION_EVENT.SUBMIT_FORM, this.initiateSubmit);
        this.eventBus.off(ANNOTATION_EVENT.COMPLETE_QUIZ, this.onCompleteQuiz);
        this.eventBus.off(ANNOTATION_EVENT.NAVIGATE_SUB_ELEMENT, this.onNavigateSubElement);
        this.eventBus.off(APPLICATION_EVENT.LEVEL_CHANGED, this.onLevelChanged);
    }

    componentDidMount(...args) {
        super.componentDidMount(...args);
        this.broadcastState();
    }

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps);
        this.quizController.descendants = this.props.descendants;
        this._sections = null;
        this._questions = null;
        this._buttons = null;
        this._progress = null;
        this.initializeGrading();
        this.applyGrading();

        const updates = this.quizController.currentScene;
        
        const { properties = {} } = prevProps;
        const { showCorrect, readOnly } = this.options;
        const { recordingResponseStatus } = this.state;

        if (readOnly !== properties.readOnly) {
            updates.readOnly = readOnly;
            updates.recordingResponseStatus = readOnly ? RECORDING_STATUSES.SUCCESSFUL : RECORDING_STATUSES.NOT_STARTED;
        }

        if (showCorrect !== properties.showCorrect && recordingResponseStatus === RECORDING_STATUSES.NOT_STARTED) {
            updates.showingCorrect = showCorrect;
        }

        this.setStateJudiciously(updates);
    }

    setStateJudiciously = (updates = {}) => {
        const keys = Object.keys(updates);
        const hasChanges = (
            keys.length &&
            keys.some(key => this.state[key] !== updates[key])
        )

        if (hasChanges) {
            this.setState({
                ...updates
            })
        }
    }

    onNextItem = id => {
        if (id === this.props.id) {
            if (this.quizController.isLastScene) {
                this.onAction('onComplete');
            } else {
                const state = this.quizController.nextScene();
                this.setStateJudiciously(state);
            }
        }
    }

    onPrevItem = id => {
        if (id === this.props.id) {
            const state = this.quizController.prevScene();
            this.setStateJudiciously(state);
        }
    }

    setState(state) {
        const { id } = this.props;
        return super.setState(state, this.broadcastState);
    }

    broadcastState = () => {
        const updates = { ...this.state };

        this.eventBus.emit(ANNOTATION_EVENT.QUIZ_UPDATED, {
            id: this.props.id,
            updates
        })
    }

    initiateSubmit = id => {
        if (id === this.props.id) {
            this.onAction('onSubmit');
        }
    }

    onNavigateSubElement = ({ id, path = [], bypassHistory = false }) => {
        if (id === this.props.id) {
            const id = path[path.length - 1];
            const state = this.quizController.setScene(id, bypassHistory);
            this.setStateJudiciously(state);
        }
    }

    onFormReset = id => {
        if (id === this.props.id) {
            this.form?.reset();
        }
    }

    onCompleteQuiz = id => {
        if (id === this.props.id) {
            this.onAction('onComplete');
        }
    }

    onLevelChanged = level => {
        const { level: currentLevel } = this.props?.data?.state || {};

        if (currentLevel !== level) {
            this.resetQuiz();
            this.setState({
                currentLevel
            })
        }
    }

    resetQuiz() {
        this.form?.reset();
        this.quizController = new QuizSceneController({
            descendants: []
        });
        this.setState({
            ...this.defaultState,
            ...this.quizController.currentScene
        });
    }

    render() {
        const {
            showSectionDetails,
            requiredQuestion,
            currentQuestion,
            preSubmit,
            postSubmit,
            recordingResponseStatus
        } = this.state;
        const { styles = {}, id } = this.props;
        const answered = this.formData[currentQuestion];
        const { scenes = [] } = this.quizController;
        const { QUIZ } = COMPOSITION_TYPES;
        const { color = 'gray' } = this.getElementStyles(QUIZ.QUESTION_TITLE);

         return (
            <form
                {...this.rootAttrs}
                data-required={requiredQuestion && !answered}
                data-section-details={showSectionDetails}
                data-record-status={recordingResponseStatus}
            >
                {
                    scenes.map(s => s.render(
                            {
                                ...this.state,
                                quizId: id,
                                eventBus: this.eventBus,
                                answers: this.formData,
                                styles,
                            }
                        )
                    )
                }
                <hr className="content-separator" style={{ backgroundColor:color }}/>
                {
                    this.progress && 
                        <QuizProgress
                            {...this.progress}
                            describeProgress={!postSubmit}
                            currentAnswer={this.currentAnswerIndex}
                            progress={this.quizController.currentProgress}
                            segments={this.quizController.totalProgress}
                        />
                }
                {/* We're not sure if we want to delete this, keeping it here for reference.           
                { preSubmit && <PreSubmitNotice {...this.state} /> } */}
                <Buttons
                    quizId={id}
                    {...this.state}
                    styles={styles}
                    bus={this.eventBus}
                    buttons={this.buttons}
                    answered={answered}
                />
            </form>
        )
    }

    onChange = e => {
        const { readOnly } = this.options;
        this.calculator.currentAnswers = this.formData;
        this.applyGrading();

        this.eventBus.emit(
            APPLICATION_EVENT.FORM_DATA_CHANGE, {
                [this.props.id]: this.formData
            }
        );

        if (!this.interacted) {
            this.eventBus.emit(
                ANNOTATION_EVENT.LIFECYCLE_CHANGE, {
                    id: this.props.id,
                    status: readOnly ? LIFECYCLES.VISITED : LIFECYCLES.INTERACTED
                }
            );

            this.eventBus.emit(PLAYER_CONTROL_EVENT.PAUSE, true);
            this.interacted = true;
        }
        
        this.forceUpdate();
    }

    applyGrading() {
        const {
            passing,
            score,
            answeredCorrectly,
            answeredIncorrectly,
            unansweredQuestions,
        } = this.calculator;

        if (
            this.state.passing !== passing ||
            this.state.currentScore !== score ||
            !is.equal(this.state.answeredCorrectly, answeredCorrectly, { sortArrays: true}) ||
            !is.equal(this.state.answeredIncorrectly, answeredIncorrectly, { sortArrays: true}) ||
            !is.equal(this.state.unansweredQuestions, unansweredQuestions, { sortArrays: true})
        ) {
            // Ensure Form State changes have been propagated before applying Quiz State changes
            requestAnimationFrame(() => {
                this.setState({
                    passing,
                    answeredCorrectly,
                    answeredIncorrectly,
                    unansweredQuestions,
                    currentScore: score
                })
            });

            this.eventBus.emit(APPLICATION_EVENT.SET_SCORE, score);
        }
    }

    onSubmit = e => {
        const {
            remainingRetries,
            preSubmit,
            passing,
            recordingResponseStatus,
            hasSummary,
            unansweredQuestions,
        } = this.state;
        const { highlightCorrect = false, showCorrect = false } = this.options

        let updates;

        if (!passing && remainingRetries > 0 && !preSubmit) {
            updates = {
                remainingRetries: remainingRetries - 1,
                preSubmit: true
            }
        } else if (unansweredQuestions.length) {
            updates = {
                preSubmit: true
            }
        } else {
            if (
                this.options.recordResponses && (
                    recordingResponseStatus === RECORDING_STATUSES.NOT_STARTED ||
                    recordingResponseStatus === RECORDING_STATUSES.FAILED
                )
            ) {
                this.recordResponse();
                updates = {
                    recordingResponseStatus: RECORDING_STATUSES.IN_PROGRESS,
                };
            } else if (
                !this.options.recordResponses &&
                recordingResponseStatus !== RECORDING_STATUSES.NOT_APPLICABLE
            ) {
                updates = {
                    showingCorrect: showCorrect || highlightCorrect,
                    recordingResponseStatus: RECORDING_STATUSES.NOT_APPLICABLE                    
                }

                if (!this.quizController.isLastScene) {
                    updates = {
                        ...updates,
                        ...this.quizController.lastScene()
                    }
                } else {
                    this.onAction('onComplete');
                }
            }

            // must check BEFORE `lastScene()` because `lastScene()` changes controller's scene index
            if (
                this.quizController.canComplete &&
                recordingResponseStatus !== RECORDING_STATUSES.NOT_STARTED &&
                recordingResponseStatus !== RECORDING_STATUSES.FAILED &&
                recordingResponseStatus !== RECORDING_STATUSES.IN_PROGRESS
            ) {
                this.onAction('onComplete');
            }

            if (hasSummary) {
                updates = {
                    ...updates,
                    ...this.quizController.lastScene()
                }
            }
        }

        if (updates) {
            this.setState(updates);
        }

    }

    recordResponse() {
        const { hasSummary } = this.state;
        const response = formatResponse({
            formData: this.formData,
            quizConfig: this.props,
            quizState: this.state,
            questions: this.questions
        });
        const quizContext = {
            annotationRefId: this.internal.refId
        };
        const { highlightCorrect = false, showCorrect = false } = this.options;

        this.eventBus.emit(
            ANNOTATION_EVENT.RECORD_QUIZ_RESPONSE,
            {
                response,
                quizContext
            }
        );
        
        this.eventBus.emit(ANNOTATION_EVENT.LIFECYCLE_CHANGE, {
            id: this.props.id,
            status: !this.state.passing ? LIFECYCLES.FAILED : LIFECYCLES.SUCCESS 
        })

        this.eventBus.once(
            ANNOTATION_EVENT.RECORD_QUIZ_RESPONSE_SUCCEED,
            () => {
                let updates = {
                    submitError: false,
                    showingCorrect: showCorrect || highlightCorrect,
                    recordingResponseStatus: RECORDING_STATUSES.SUCCESSFUL
                };

                if (hasSummary) {
                    updates = {
                        ...updates,
                        ...this.quizController.lastScene()
                    };
                }

                this.setState(updates);
            }
        );

        this.eventBus.once(
            ANNOTATION_EVENT.RECORD_QUIZ_RESPONSE_FAILED,
            () => this.setState({
                showingCorrect: showCorrect || highlightCorrect,
                recordingResponseStatus: RECORDING_STATUSES.FAILED,
                submitError: true
            })
        );
    }

    onRetryQuiz = id => {
        if (id === this.props.id) {
            const state = this.quizController.restart();

            this.setState({
                preSubmit: false,
                ...state
            })
        }
    }

    applyActions(...args) {
        const applied = super.applyActions(...args);

        return {
            ...applied,
            onChange: (...args) => {
                this.onChange(...args);

                if (is.fn(applied.onChange)) {
                    applied.onChange(...args);
                }
            },
            // Need to handle onClick for manually change inputs
            onClick: (...args) => {
                this.onChange(...args);

                if (is.fn(applied.onClick)) {
                    applied.onClick(...args);
                }
            }
        }
    }

    get sections() {
        if (!this._sections) {
            const { descendants = [] } = this.props;
            this._sections = (
                descendants
                    .filter(d => d.type === 'quizsection')
            )
        }

        return this._sections;
    }

    get questions() {
        if (!this._questions) {
            this._questions = (
                this.sections
                    .flatMap(s => s.descendants)
                    .filter(d => d.type === 'question')
            )
        }

        return this._questions
    }

    get buttons() {
        if (!this._buttons) {
            const { descendants = [] } = this.props;
            this._buttons = (
                descendants
                    .filter(d => 
                        d.type === 'pop' &&
                        d.intent?.endsWith('Button')
                    )
            )
        }

        return this._buttons;
    }

    get progress() {
        if (!this._progress) {
            const { descendants = [] } = this.props;
            this._progress = descendants.find(d => d.type === 'progress');
        }

        return this._progress;
    }

    get rootAttrs() {
        return {
            ref: ref => this.form = ref,
            style: this.getElementStyles(),
            className: `${this.classes} quiz-annotation`,
            ...this.dataAttrs,
            ...this.applyActions(this.actions)
        }
    }

    get formData() {
        if (this.form) {
            const formData = formToObject(this.form);

            // "Sets" of checkboxes should serialize their selected items as arrays
            const groupedCheckbox = (
                Array
                    .from(this.form.querySelectorAll('input[type="checkbox"]'))
                    .filter(c => c.checked)
                    .reduce((grouped, checkbox) => {
                        const { name, value } = checkbox;

                        if (name in grouped) {
                            grouped[name].push(value);
                        } else {
                            grouped[name] = [value];
                        }

                        return grouped;
                    }, {})
            )

            Array
                .from(this.form.querySelectorAll('input[type="range"]'))
                .forEach(input => {
                    if (!input.dataset.hasAnswer || input.dataset.hasAnswer === 'false') {
                        delete formData[input.id];
                    }
                })

            this._formData = {
                ...formData,
                ...groupedCheckbox
            }

            return this._formData;
        }

        return {};
    }

    get defaultState() {
        const { id } = this.props;
        const {
            allowedRetries = 0,
            showCorrect = false,
            readOnly = false
        } = this.options;

        return {
            // portions of this state are set by the Scene Controller instance
            startTime: now(),
            remainingRetries: allowedRetries,
            currentScore: 0,
            passing: false,
            progress: 0,
            readOnly,
            showingFeedback: false,
            showingCorrect: showCorrect,
            preSubmit: false,
            postSubmit: false,
            submitError: false,
            answeredCorrectly: [],
            answeredIncorrectly: [],
            answeredQuestions: [],
            unansweredQuestions: [],
            recordingResponseStatus: readOnly ? RECORDING_STATUSES.SUCCESSFUL : RECORDING_STATUSES.NOT_STARTED
        }
    }

    get currentAnswerIndex() {
        if (this.form) {
            const { currentQuestion = '' } = this.state;
            const currentAnswer = this.formData[currentQuestion];

            return (
                Array.from(
                    this.form.querySelectorAll(`input[name="${currentQuestion}"]`)
                ).findIndex(i => i.value === currentAnswer)
            )
        }

        return 0;
    }
}